import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { ArrowLeft, MapPin, Eye, Share2, Clock, Package, PhoneCall, CheckCircle2, ShoppingBag, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import NotificationBell from '@/components/NotificationBell';
import BottomNav from '@/components/BottomNav';
import ConfirmSheet from '@/components/ConfirmSheet';
import Avatar from '@/components/Avatar';

/**
 * Detail page for a buyer's requirement post.
 * Any signed-in user (typically a farmer) can tap "Request Produce" to have
 * Frootex admin coordinate the deal offline.
 */
export default function RequirementDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { api, user } = useAuth();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [callbackReq, setCallbackReq] = useState(null);
  const [requesting, setRequesting] = useState(false);
  const [adminDeleteOpen, setAdminDeleteOpen] = useState(false);
  const [adminDeleting, setAdminDeleting] = useState(false);
  const [ownerDeleteOpen, setOwnerDeleteOpen] = useState(false);

  const isAdmin = user?.role === 'admin';
  const isOwner = user && post && post.user_id === user._id;

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get(`/requirements/${id}`);
        setPost(data);
      } catch {
        toast.error('Requirement not found');
        navigate('/');
      } finally {
        setLoading(false);
      }
    };
    const checkCallback = async () => {
      try {
        const { data } = await api.get('/callback-requests/mine', { params: { post_id: id } });
        setCallbackReq(data.request || null);
      } catch { /* noop */ }
    };
    load();
    checkCallback();
  }, [api, id, navigate]);

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({ title: post.title, url: window.location.href });
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied');
    }
  };

  const handleRequestProduce = async () => {
    setRequesting(true);
    try {
      const { data } = await api.post('/callback-requests', {
        post_id: id,
        target_type: 'requirement',
      });
      setCallbackReq(data.request);
      toast.success(
        data.already_requested
          ? 'You already offered to serve this requirement'
          : 'Sent! Frootex will coordinate with the buyer and call you shortly.'
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send request');
    } finally {
      setRequesting(false);
    }
  };

  const handleAdminDelete = async () => {
    setAdminDeleting(true);
    try {
      await api.delete(`/requirements/${id}`);
      toast.success('Requirement deleted');
      navigate(-1);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    } finally {
      setAdminDeleting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const diff = Math.floor((Date.now() - d.getTime()) / 60000);
    if (diff < 60) return `${Math.max(diff, 1)}m ago`;
    if (diff < 1440) return `${Math.floor(diff / 60)}h ago`;
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  };

  if (loading) {
    return (
      <div className="w-full max-w-md mx-auto min-h-screen bg-[#F6F8FB] md:max-w-xl lg:max-w-2xl border-x border-[#E4E8EF]">
        <div className="p-4 space-y-4 animate-pulse">
          <div className="h-6 bg-[#EFF2F7] rounded w-3/4" />
          <div className="h-4 bg-[#EFF2F7] rounded w-1/2" />
          <div className="h-40 bg-[#EFF2F7] rounded-2xl" />
        </div>
      </div>
    );
  }

  if (!post) return null;

  return (
    <div
      className="w-full max-w-md mx-auto min-h-screen bg-[#F6F8FB] md:max-w-xl lg:max-w-2xl border-x border-[#E4E8EF]"
      data-testid="requirement-detail-page"
    >
      {/* Top bar */}
      <div className="sticky top-0 z-40 bg-[#F6F8FB]/85 backdrop-blur-xl border-b border-[#E4E8EF] px-4 py-3 flex items-center justify-between">
        <button
          data-testid="back-btn"
          onClick={() => navigate(-1)}
          className="w-10 h-10 rounded-full bg-[#EFF2F7] flex items-center justify-center border border-[#E4E8EF]"
          aria-label="Back"
        >
          <ArrowLeft className="w-5 h-5 text-[#0B1620]" />
        </button>
        <div className="flex gap-2">
          <NotificationBell />
          <button
            data-testid="share-btn"
            onClick={handleShare}
            className="w-10 h-10 rounded-full bg-[#EFF2F7] flex items-center justify-center border border-[#E4E8EF]"
            aria-label="Share"
          >
            <Share2 className="w-4 h-4 text-[#0B1620]" />
          </button>
        </div>
      </div>

      <div className="p-4 pb-28 space-y-5">
        {/* Requirement badge + category */}
        <div className="flex items-center gap-2 animate-fade-in-up">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#0B4F3F]/10 text-[#0B4F3F] text-[10px] font-bold uppercase tracking-wider">
            <ShoppingBag className="w-3 h-3" />
            Requirement
          </span>
          <span className="px-2 py-1 rounded-full bg-[#EFF2F7] text-[10px] font-semibold text-[#0B1620] uppercase tracking-wider">
            {post.category}
          </span>
          {!post.is_open && (
            <span className="px-2 py-1 rounded-full bg-[#B91C1C]/10 text-[#B91C1C] text-[10px] font-bold uppercase tracking-wider">
              Closed
            </span>
          )}
        </div>

        {/* Image (optional) */}
        {post.image && (
          <div className="w-full h-56 md:h-64 rounded-3xl overflow-hidden bg-[#EFF2F7] animate-fade-in-up stagger-1">
            <img src={post.image} alt={post.title} className="w-full h-full object-cover" />
          </div>
        )}

        {/* Title + meta */}
        <div className="animate-fade-in-up stagger-2">
          <h1 className="text-2xl font-bold text-[#0B1620] font-['Bricolage_Grotesque'] tracking-tight">
            {post.title}
          </h1>
          <div className="flex items-center gap-4 mt-2 text-xs text-[#5B6675]">
            <span className="flex items-center gap-1"><Eye className="w-3.5 h-3.5" /> {post.views || 0}</span>
            <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {formatDate(post.created_at)}</span>
          </div>
        </div>

        {/* Offer + quantity */}
        <div className="grid grid-cols-2 gap-3 animate-fade-in-up stagger-3">
          <div className="bg-[#F6F8FB] border border-[#0B4F3F]/25 rounded-2xl p-3">
            <p className="text-[10px] uppercase tracking-wider text-[#5B6675]">Buyer&apos;s offer</p>
            <p className="text-lg font-bold text-[#0B4F3F] font-['Bricolage_Grotesque'] mt-0.5">
              ₹{post.price_per_unit}
              <span className="text-[11px] font-normal text-[#5B6675]">/{post.unit}</span>
            </p>
          </div>
          <div className="bg-[#F6F8FB] border border-[#E4E8EF] rounded-2xl p-3 flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-[#EFF2F7] flex items-center justify-center">
              <Package className="w-4 h-4 text-[#0B4F3F]" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-[#5B6675]">Quantity</p>
              <p className="text-sm font-semibold text-[#0B1620]">{post.quantity} {post.unit}</p>
            </div>
          </div>
        </div>

        {/* Description */}
        {post.description && (
          <div className="animate-fade-in-up stagger-3">
            <h3 className="text-sm font-semibold text-[#0B1620] mb-2 font-['Bricolage_Grotesque']">Description</h3>
            <p className="text-sm text-[#5B6675] leading-relaxed whitespace-pre-line">{post.description}</p>
          </div>
        )}

        {/* Location */}
        {post.location && (
          <div className="flex items-center gap-2 text-sm text-[#5B6675] animate-fade-in-up stagger-4">
            <MapPin className="w-4 h-4 text-[#0B4F3F]" />
            {post.location}
          </div>
        )}

        {/* Buyer card */}
        <div className="bg-[#F6F8FB] rounded-2xl border border-[#E4E8EF] p-4 animate-fade-in-up stagger-4" data-testid="buyer-card">
          <div className="flex items-center gap-3">
            <Avatar name={post.user_name} src={post.user_avatar} size={48} className="border-2 border-[#E4E8EF]" />
            <div className="flex-1 min-w-0">
              <p className="text-base font-semibold text-[#0B1620] truncate">{post.user_name || 'Buyer'}</p>
              <p className="text-xs text-[#5B6675] capitalize">
                {post.user_role === 'farmer' ? 'Farmer looking to buy' : 'Buyer'}
              </p>
            </div>
          </div>
        </div>

        {/* Owner controls */}
        {isOwner && (
          <button
            type="button"
            data-testid="owner-delete-btn"
            onClick={() => setOwnerDeleteOpen(true)}
            className="w-full h-11 rounded-full border border-[#B91C1C]/30 text-[#B91C1C] text-sm font-semibold bg-[#F6F8FB] hover:bg-[#B91C1C]/8 transition-colors animate-fade-in-up"
          >
            Close & delete this requirement
          </button>
        )}

        {/* Admin moderation */}
        {isAdmin && !isOwner && (
          <div
            data-testid="admin-req-moderation-strip"
            className="bg-[#F6F8FB] rounded-2xl border border-[#0B4F3F]/25 p-3 animate-fade-in-up"
          >
            <p className="text-[10px] uppercase tracking-[0.14em] text-[#0B4F3F] font-semibold mb-2 flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-[#0B4F3F]" /> Admin controls
            </p>
            <button
              type="button"
              data-testid="admin-req-delete-btn"
              onClick={() => setAdminDeleteOpen(true)}
              disabled={adminDeleting}
              className="w-full h-10 rounded-full text-[12px] font-semibold flex items-center justify-center gap-1.5 border border-[#DC2626]/25 text-[#DC2626] bg-[#F6F8FB] hover:bg-[#DC2626]/8 transition-colors disabled:opacity-50"
            >
              <Trash2 className="w-3.5 h-3.5" /> Delete requirement
            </button>
          </div>
        )}

        {/* Primary CTA */}
        {!isOwner && !isAdmin && (
          <div className="space-y-3 animate-fade-in-up stagger-5 pb-2">
            {callbackReq ? (
              <div
                data-testid="req-callback-awaiting-state"
                className="w-full rounded-2xl border border-[#15803D]/30 bg-[#15803D]/5 px-4 py-4 flex items-start gap-3"
              >
                <div className="w-9 h-9 rounded-full bg-[#15803D]/15 flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="w-5 h-5 text-[#15803D]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[#15803D] font-['Bricolage_Grotesque']">
                    Waiting for the Frootex team
                  </p>
                  <p className="text-xs text-[#5B6675] mt-0.5 leading-relaxed">
                    We&apos;ve received your offer and will call you on{' '}
                    <span className="font-semibold text-[#0B1620]">{callbackReq.user_phone || 'your registered number'}</span>{' '}
                    shortly to coordinate with the buyer.
                  </p>
                  {callbackReq.status === 'contacted' && (
                    <p className="text-[11px] text-[#15803D] mt-1 font-medium">
                      Our team has reached out — please check your phone.
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <Button
                data-testid="request-produce-btn"
                onClick={handleRequestProduce}
                disabled={!post.is_open || requesting}
                className="w-full h-12 bg-[#0B4F3F] hover:bg-[#083D31] text-[#F6F8FB] rounded-full font-semibold text-base transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <PhoneCall className="w-4 h-4 mr-2" />
                {!post.is_open
                  ? 'Requirement Closed'
                  : requesting
                    ? 'Sending offer…'
                    : 'Request Produce'}
              </Button>
            )}
            {!callbackReq && post.is_open && (
              <p className="text-[11px] text-center text-[#5B6675]/80">
                Our team will verify your produce and connect you with the buyer.
              </p>
            )}
          </div>
        )}
      </div>

      <BottomNav />

      {/* Admin delete confirm */}
      <ConfirmSheet
        open={adminDeleteOpen}
        onOpenChange={setAdminDeleteOpen}
        title="Delete this requirement?"
        description={`"${post.title}" will be removed from the marketplace forever. This action cannot be undone.`}
        confirmLabel="Delete requirement"
        cancelLabel="Keep it"
        destructive
        icon={<Trash2 className="w-6 h-6" strokeWidth={2.2} />}
        onConfirm={handleAdminDelete}
        loading={adminDeleting}
        testId="admin-req-delete"
      />

      {/* Owner delete confirm */}
      <ConfirmSheet
        open={ownerDeleteOpen}
        onOpenChange={setOwnerDeleteOpen}
        title="Close & delete this requirement?"
        description="Farmers won't be able to see or respond to this requirement anymore."
        confirmLabel="Delete"
        cancelLabel="Keep it"
        destructive
        icon={<Trash2 className="w-6 h-6" strokeWidth={2.2} />}
        onConfirm={handleAdminDelete}
        loading={adminDeleting}
        testId="owner-req-delete"
      />
    </div>
  );
}
